/**
 * Permission slugs aligned with super-admin RBAC seed data
 * (`Backend/super-admin/prisma/json/core-data/permissions/*.json`).
 * Use with `hasPermission` pipe / `PermissionService` — not admin tenant slugs (`list_roles`, etc.).
 */
export const SUPER_ADMIN_PERMISSION_SLUGS = {
  /** Dashboard (`/dashboard`) */
  DASHBOARD_VIEW: 'dashboard_view',

  /** Platform health page — turn background sampling on/off */
  PLATFORM_HEALTH_MANAGE: 'platform_health_manage',

  /** PD sessions — dashboard volumes, trends and activity feed */
  PD_SESSION_LIST: 'pd_session_list',
  PD_SESSION_VIEW: 'pd_session_view',
  PD_SESSION_EXPORT: 'export_pd_session',

  /** Manage Clients (`/manage-client`) */
  TENANT_LIST: 'list_tenants',
  TENANT_ARCHIVED_LIST: 'list_archived_tenants',
  TENANT_ADD: 'add_tenant',
  TENANT_UPDATE: 'update_tenant',
  TENANT_TOGGLE_STATUS: 'toggle_tenant_status',
  TENANT_DELETE: 'delete_tenant',
  TENANT_RESTORE: 'restore_tenant',
  TENANT_RESEND_LINK: 'resend_tenant_link',
  TENANT_APPLY_MIGRATION: 'apply_tenant_migration',
  TENANT_APPLY_SEEDER: 'apply_tenant_seeder',
  TENANT_EXPORT: 'export_tenant',
  TENANT_VIEW: 'view_tenant',

  /** Staff portal users (`/manage-admin-users`) — `users.json` seed */
  USER_LIST: 'list_users',
  USER_ARCHIVED_LIST: 'user_archived_list',
  USER_ADD: 'add_user',
  USER_UPDATE: 'update_user',
  USER_DELETE: 'delete_user',
  USER_TOGGLE_STATUS: 'toggle_user_status',
  USER_IMPORT: 'import_user',
  USER_RESTORE: 'restore_user',
  USER_EXPORT: 'export_user',
  USER_VIEW: 'view_user',
  USER_RESEND_LINK: 'resend_user_link',

  /** System Preferences — roles */
  ROLE_LIST: 'role_list',
  ROLE_ARCHIVED_LIST: 'role_archived_list',
  ROLE_ADD: 'create_role',
  ROLE_UPDATE: 'update_role',
  ROLE_DELETE: 'delete_role',
  ROLE_RESTORE: 'restore_role',
  ROLE_TOGGLE_STATUS: 'toggle_role_status',
  ROLE_MANAGE_PERMISSIONS: 'manage_role_permissions',
  ROLE_UPDATE_PERMISSIONS: 'update_role_permissions',
  /** UI checks: either manage or update slug is enough */
  ROLE_ATTACH_PERMISSION: 'manage_role_permissions',

  /** System Preferences — Stimulate Agent (`simulate-agent.json` seed) */
  SIMULATE_AGENT_SETTINGS_LIST: 'list_simulate_agent_settings',
  SIMULATE_AGENT_SETTINGS_UPDATE: 'update_simulate_agent_settings',

  /** Manage Agent */
  AGENT_LIST: 'agent_list',
  AGENT_ARCHIVED_LIST: 'agent_archived_list',
  AGENT_ADD: 'create_agent',
  AGENT_UPDATE: 'update_agent',
  AGENT_DELETE: 'delete_agent',
  AGENT_RESTORE: 'restore_agent',
  AGENT_TOGGLE_STATUS: 'toggle_agent_status',
  AGENT_MANAGE_PERMISSIONS: 'manage_agent_permissions',
  AGENT_UPDATE_PERMISSIONS: 'update_agent_permissions',
  AGENT_VIEW: 'view_agent',
  AGENT_EXPORT: 'export_agent',
  AGENT_ASSIGN_TO_CLIENT: 'assign_to_client',
  AGENT_RETRAIN: 'retrain_agent',
  AGENT_GO_LIVE: 'go_live_agent',

  /** Audit Trail (`/audit-trail`) — `audit-trail.json` seed */
  AUDIT_TRAIL_LIST: 'audit_trail_list',
  AUDIT_TRAIL_VIEW: 'audit_trail_view',
  AUDIT_TRAIL_EXPORT: 'export_audit_trail',

  /** Billing (`/billing`) — `billing.json` seed */
  BILLING_VIEW: 'view_billing',
  BILLING_MANAGE: 'manage_billing',

  /** Reports (`/reports`) — `report.json` seed */
  REPORT_LIST: 'report_list',
  REPORT_EXPORT: 'export_report',
  REPORT_VIEW: 'view_report',

  /** Rule Packs (`/rule-packs`) — `rule-packs.json` seed */
  RULE_PACK_LIST: 'rule_pack_list',

  /** Product documents (`/products`) — `product-documents.json` seed */
  PRODUCT_DOCUMENT_LIST: 'product_document_list',
  PRODUCT_DOCUMENT_UPDATE: 'product_document_update',

  /** Workflow engine (`/workflows`) — `workflow.json` seed. Builder UI lands in Phase 9. */
  WORKFLOW_LIST: 'workflow_list',
  WORKFLOW_VIEW: 'view_workflow',
  WORKFLOW_CREATE: 'create_workflow',
  WORKFLOW_PUBLISH: 'publish_workflow',

  /** LLM configuration (`/llm-config`, `/llm-models`) */
  LLM_CONFIG_MANAGE: 'llm_config_manage',

  /** AI / LLM cost monitoring (`/llm-cost`, `/llm-usage`, `/llm-pd-cost`) */
  LLM_COST_VIEW: 'llm_cost_view',
  /** Editing a price restates the cost of every future call, so it is held apart from reading. */
  LLM_PRICING_MANAGE: 'llm_pricing_manage',

  /** Integrations (`/integrations`) — `integrations.json` seed */
  INTEGRATION_LIST: 'integration_list',
  /** Rotating a secret takes a client's endpoint down until they redeploy, so it is held apart
      from merely viewing the list. */
  WEBHOOK_ROTATE_SECRET: 'rotate_webhook_secret',

  /** Organization Details (`/system-preferences` → Organization Details) — `organization-details.json` seed */
  ORGANIZATION_DETAILS_LIST: 'list_organization_details',
  ORGANIZATION_DETAILS_UPDATE: 'update_organization_details',

  /** SMTP Settings (`/system-preferences` → Manage SMTP Settings) — `smtp-settings.json` seed */
  SMTP_SETTINGS_LIST: 'list_smtp_settings',
  SMTP_SETTINGS_UPDATE: 'update_smtp_settings',

  /** Email templates (`/system-preferences` → Email Template) — `email-template.json` seed */
  EMAIL_TEMPLATE_LIST: 'list_email_templates',
  EMAIL_TEMPLATE_ADD: 'add_email_template',
  EMAIL_TEMPLATE_UPDATE: 'update_email_template',
  EMAIL_TEMPLATE_DELETE: 'delete_email_template',
  EMAIL_TEMPLATE_TOGGLE: 'toggle_email_template',
  EMAIL_TEMPLATE_RESTORE: 'restore_email_template',

  /** Subscriptions (`/subscriptions`) — `subscription.json` seed */
  SUBSCRIPTION_LIST: 'list_subscriptions',
  SUBSCRIPTION_ARCHIVED_LIST: 'list_archived_subscriptions',
  SUBSCRIPTION_ADD: 'add_subscription',
  SUBSCRIPTION_VIEW: 'view_subscription',
  SUBSCRIPTION_UPDATE: 'update_subscription',
  SUBSCRIPTION_DELETE: 'delete_subscription',
  SUBSCRIPTION_RESTORE: 'restore_subscription',
} as const;

/** Slugs accepted for role permission attach UI (view + save). */
export const ROLE_PERMISSION_ATTACH_SLUGS = [
  SUPER_ADMIN_PERMISSION_SLUGS.ROLE_MANAGE_PERMISSIONS,
  SUPER_ADMIN_PERMISSION_SLUGS.ROLE_UPDATE_PERMISSIONS,
] as const;
